import React, { useState, useEffect } from 'react';
import { Shield, ShieldCheck, ShieldOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export default function ClientSecurityPage() {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [setupStep, setSetupStep] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`${API_URL}/auth/mfa/status`, { headers: getAuthHeaders() });
      if (!res.ok) {
        if (res.status === 403) setError('This page is for client portal users only.');
        else if (res.status === 404) setError('Security (MFA) is not available. Please ensure the API has been updated and migrations run.');
        else setError('Failed to load security settings.');
        return;
      }
      const data = await res.json();
      setMfaEnabled(data.mfaEnabled === true);
    } catch (err) {
      setError('Failed to load security settings. Check the browser Network tab to see if the request to /api/auth/mfa/status failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');
      const res = await fetch(`${API_URL}/auth/mfa/setup`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to start setup.');
        setSubmitting(false);
        return;
      }
      setQrCodeDataUrl(data.qrCodeDataUrl);
      setSetupStep('scan');
      setConfirmCode('');
    } catch (err) {
      setError('Failed to start setup.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSetup = async (e) => {
    e.preventDefault();
    if (!confirmCode.trim() || confirmCode.length < 6) {
      setError('Please enter the 6-digit code from your app.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const res = await fetch(`${API_URL}/auth/mfa/confirm`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code: confirmCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Invalid code.');
        setSubmitting(false);
        return;
      }
      setSuccess('Two-factor authentication is now enabled.');
      setSetupStep(null);
      setMfaEnabled(true);
      setConfirmCode('');
    } catch (err) {
      setError('Failed to confirm.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!disableCode.trim() || disableCode.length < 6) {
      setError('Please enter your current 6-digit code to disable MFA.');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const res = await fetch(`${API_URL}/auth/mfa/disable`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ code: disableCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to disable.');
        setSubmitting(false);
        return;
      }
      setSuccess('Two-factor authentication has been disabled.');
      setMfaEnabled(false);
      setDisableCode('');
    } catch (err) {
      setError('Failed to disable.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelSetup = () => {
    setSetupStep(null);
    setQrCodeDataUrl('');
    setConfirmCode('');
    setError('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">Loading security settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Security</h1>
        <p className="text-gray-600 mt-1">Manage two-factor authentication for your client portal account.</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          {mfaEnabled ? (
            <ShieldCheck className="w-10 h-10 text-green-600 shrink-0 mt-0.5" />
          ) : (
            <Shield className="w-10 h-10 text-gray-400 shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">Two-factor authentication (2FA)</h2>
            <p className="text-sm text-gray-600 mt-1">
              {mfaEnabled
                ? 'Your account is protected with an authenticator app. You will be asked for a code when you sign in.'
                : 'Add an extra layer of security by requiring a code from your phone when you sign in.'}
            </p>
            {mfaEnabled ? (
              <div className="mt-4">
                <p className="text-sm text-gray-700 mb-2">To turn off 2FA, enter your current code:</p>
                <form onSubmit={handleDisable} className="flex gap-2 flex-wrap items-end">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-center tracking-widest"
                    placeholder="000000"
                  />
                  <button
                    type="submit"
                    disabled={submitting || disableCode.length < 6}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    <ShieldOff size={18} />
                    Disable 2FA
                  </button>
                </form>
              </div>
            ) : setupStep === 'scan' ? (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-gray-700">Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.):</p>
                {qrCodeDataUrl && (
                  <div className="inline-block p-2 bg-white border border-gray-200 rounded-lg">
                    <img src={qrCodeDataUrl} alt="QR code for 2FA" width={180} height={180} />
                  </div>
                )}
                <form onSubmit={handleConfirmSetup} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Enter the 6-digit code to confirm</label>
                  <div className="flex gap-2 flex-wrap items-center">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={confirmCode}
                      onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, ''))}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-center tracking-widest"
                      placeholder="000000"
                    />
                    <button
                      type="submit"
                      disabled={submitting || confirmCode.length < 6}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {submitting ? 'Verifying...' : 'Enable 2FA'}
                    </button>
                    <button
                      type="button"
                      onClick={cancelSetup}
                      className="text-sm text-gray-600 hover:text-gray-900"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleStartSetup}
                disabled={submitting}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Starting...' : 'Enable two-factor authentication'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
